import { Injectable } from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { MapSymbolFactory } from './map-symbol-factory.service';
import { ImageProcessingService } from './image-processing.service';

/**
 * Configuration for picture marker layer
 */
export interface PictureLayerConfig {
  showPictures: boolean;
}

/**
 * Context for picture click handlers
 */
export interface PictureClickContext {
  onPictureClick: (graphic: any, event: any) => void;
  onSameLocationClick: (graphic: any) => void;
}

/**
 * Processed symbol data for a picture or picture group
 */
interface PictureSymbolData {
  uuid: string;
  symbol: any;
  attributes: any;
  pictures?: any[];
}

/**
 * PictureMarkerService
 *
 * Handles all picture marker drawing and management:
 * - Drawing individual picture markers with thumbnails
 * - Drawing same-location markers (2+ pictures at same coords)
 * - Managing FeatureLayer lifecycle
 * - Handling clustering configuration
 * - Zoom-based cluster radius adjustments
 *
 * Extracted from SitesLocationMapComponent (Phase 4.9)
 */
@Injectable({
  providedIn: 'root'
})
export class PictureMarkerService {
  // Layer reference
  private pictureFeatureLayer: FeatureLayer | null = null;

  // Hash for change detection
  private previousPictureMarkersHash: string = '';

  // Map for storing pictures by marker UUID (for click handling)
  private markerPicturesMap: Map<string, any[]> = new Map();

  // Track zoom watcher handle
  private zoomWatcherHandle: any = null;

  constructor(
    private mapSymbolFactory: MapSymbolFactory,
    private imageProcessingService: ImageProcessingService
  ) {}

  /**
   * Get the current picture layer
   */
  getLayer(): FeatureLayer | null {
    return this.pictureFeatureLayer;
  }

  /**
   * Get pictures for a specific marker UUID
   */
  getPicturesForMarker(uuid: string): any[] | undefined {
    return this.markerPicturesMap.get(uuid);
  }

  /**
   * Get the marker pictures map (for sync with component)
   */
  getMarkerPicturesMap(): Map<string, any[]> {
    return this.markerPicturesMap;
  }

  /**
   * Group pictures by exact location (same lat/lng coordinates)
   * Returns map of location key -> array of pictures at that location
   */
  groupPicturesByLocation(pictures: any[]): Map<string, any[]> {
    const locationGroups = new Map<string, any[]>();

    pictures.forEach(picture => {
      const lat = parseFloat(picture.latitude);
      const lng = parseFloat(picture.longitude);

      // Create location key with 6 decimal precision (approx 10cm accuracy)
      const locationKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

      if (!locationGroups.has(locationKey)) {
        locationGroups.set(locationKey, []);
      }
      locationGroups.get(locationKey)!.push(picture);
    });

    return locationGroups;
  }

  /**
   * Calculate cluster radius based on zoom level
   * Higher zoom (zoomed in) = smaller radius (less clustering)
   */
  calculateClusterRadius(zoom: number): string {
    if (zoom >= 21) return '0px';
    if (zoom < 13) return '200px';
    if (zoom < 15) return '180px';
    if (zoom < 17) return '150px';
    if (zoom < 19) return '120px';
    return '100px';
  }

  /**
   * Draw picture markers on the map
   * @param mapView The ArcGIS MapView instance
   * @param pictureMarkers Array of picture marker data
   * @param config Layer configuration
   * @param clickContext Click handler callbacks
   */
  async drawPictureMarkers(
    mapView: __esri.MapView,
    pictureMarkers: any[],
    config: PictureLayerConfig,
    clickContext: PictureClickContext
  ): Promise<void> {
    if (!mapView) {
      console.warn('Map view not ready, skipping picture marker drawing');
      return;
    }

    // Remove existing layer if pictures are disabled
    if (!config.showPictures) {
      this.removeLayer(mapView);
      return;
    }

    // Filter out pictures without valid coordinates
    const validPictures = pictureMarkers.filter(pic =>
      pic.latitude != null && pic.longitude != null
    );

    if (validPictures.length === 0) {
      this.removeLayer(mapView);
      return;
    }

    // Create a hash of the picture data to detect changes
    const currentPicturesHash = validPictures
      .map(p => `${p.uuid}-${p.longitude}-${p.latitude}`)
      .sort()
      .join('|');

    // Check if data has actually changed
    if (currentPicturesHash === this.previousPictureMarkersHash && this.pictureFeatureLayer) {
      return;
    }

    this.previousPictureMarkersHash = currentPicturesHash;

    // Process pictures into symbols
    const allSymbols = await this.processPictures(validPictures);

    // Create graphics for FeatureLayer
    const graphics = this.createGraphics(allSymbols);

    // Check if layer already exists
    if (this.pictureFeatureLayer) {
      // Update source in place (prevents flickering)
      (this.pictureFeatureLayer.source as any).removeAll();
      (this.pictureFeatureLayer.source as any).addMany(graphics);
      return;
    }

    // Create new FeatureLayer
    this.pictureFeatureLayer = this.createFeatureLayer(graphics, allSymbols);

    // Setup click handler
    this.setupClickHandler(mapView, clickContext);

    // Setup zoom watcher
    this.setupZoomWatcher(mapView);

    // Add layer to map
    mapView.map.add(this.pictureFeatureLayer);

    // Wait for layer to load
    this.pictureFeatureLayer.when(() => {
      this.pictureFeatureLayer!.queryFeatures().then(queryResult => {
        // Layer loaded successfully
      });
    }).catch(err => {
      console.error('Picture layer failed to load:', err);
    });
  }

  /**
   * Remove the picture layer from the map
   */
  removeLayer(mapView: __esri.MapView): void {
    if (this.pictureFeatureLayer) {
      mapView.map.remove(this.pictureFeatureLayer);
      this.pictureFeatureLayer = null;
    }
    this.previousPictureMarkersHash = '';
  }

  /**
   * Process pictures into symbol data (both individual and same-location groups)
   */
  private async processPictures(validPictures: any[]): Promise<PictureSymbolData[]> {
    // Group pictures by exact location
    const locationGroups = this.groupPicturesByLocation(validPictures);

    // Separate same-location groups from individual pictures
    const sameLocationGroups: Array<{ location: string; pictures: any[] }> = [];
    const individualPictures: any[] = [];

    locationGroups.forEach((pictures, locationKey) => {
      if (pictures.length >= 2) {
        sameLocationGroups.push({ location: locationKey, pictures });
      } else {
        individualPictures.push(...pictures);
      }
    });

    // Process same-location groups
    const sameLocationPromises = sameLocationGroups.map(group =>
      this.processSameLocationGroup(group)
    );

    // Process individual pictures
    const individualPromises = individualPictures.map(picture =>
      this.processIndividualPicture(picture)
    );

    // Wait for all processing
    const sameLocationSymbols = (await Promise.all(sameLocationPromises)).filter(s => s !== null) as PictureSymbolData[];
    const pictureSymbols = await Promise.all(individualPromises);

    return [...sameLocationSymbols, ...pictureSymbols];
  }

  /**
   * Process a same-location group (2+ pictures at same coords)
   */
  private async processSameLocationGroup(
    group: { location: string; pictures: any[] }
  ): Promise<PictureSymbolData | null> {
    try {
      const firstPicture = group.pictures[0];
      const imageUrl = firstPicture.thumb_url || firstPicture.original_url;
      const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl, 35);

      // Check if all pictures have valid direction
      const allHaveDirection = group.pictures.every(p =>
        p.direction !== null && p.direction !== undefined
      );

      // Check if all pictures have the SAME direction (within 5 degrees)
      let allSameDirection = false;
      let commonDirection = 0;
      if (allHaveDirection) {
        const firstDirection = group.pictures[0].direction;
        allSameDirection = group.pictures.every(p =>
          Math.abs(p.direction - firstDirection) <= 5
        );
        if (allSameDirection) {
          commonDirection = firstDirection;
        }
      }

      const showArrow = allHaveDirection && allSameDirection;
      const rotation = showArrow ? commonDirection : 0;

      const markerUuid = `same-location-${group.location}`;

      // Store pictures in map for retrieval on click
      this.markerPicturesMap.set(markerUuid, group.pictures);

      return {
        uuid: markerUuid,
        pictures: group.pictures,
        symbol: this.mapSymbolFactory.createSameLocationMarkerSymbol(
          circularImageUrl,
          rotation,
          group.pictures.length,
          markerUuid,
          showArrow
        ),
        attributes: {
          is_same_location: 1,
          count: group.pictures.length,
          picture_uuids: group.pictures.map(p => p.uuid).join(','),
          ...firstPicture
        }
      };
    } catch (error) {
      console.error('Failed to process same-location group', group.location, error);
      return null;
    }
  }

  /**
   * Process an individual picture
   */
  private async processIndividualPicture(picture: any): Promise<PictureSymbolData> {
    try {
      const imageUrl = picture.thumb_url || picture.original_url;
      const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl, 35);

      // Store individual picture in map
      this.markerPicturesMap.set(picture.uuid, [picture]);

      const hasValidDirection = picture.direction !== null && picture.direction !== undefined;
      const showArrow = hasValidDirection;
      const rotation = hasValidDirection ? picture.direction : 0;

      return {
        uuid: picture.uuid,
        symbol: this.mapSymbolFactory.createPictureMarkerSymbol(
          circularImageUrl,
          rotation,
          picture.uuid,
          showArrow
        ),
        attributes: {
          is_same_location: 0,
          count: 1,
          ...picture
        }
      };
    } catch (error) {
      console.error('Failed to process picture', picture.uuid, error);
      // Return placeholder symbol on error
      return {
        uuid: picture.uuid,
        symbol: this.mapSymbolFactory.createPlaceholderMarkerSymbol(),
        attributes: {
          is_same_location: 0,
          count: 1,
          ...picture
        }
      };
    }
  }

  /**
   * Create graphics from symbol data
   */
  private createGraphics(allSymbols: PictureSymbolData[]): Graphic[] {
    return allSymbols.map((symbolData, index) => {
      const attrs = symbolData.attributes;
      const wgs84Point = new Point({
        longitude: attrs.longitude!,
        latitude: attrs.latitude!,
        spatialReference: { wkid: 4326 }
      });

      // Project from WGS84 to Web Mercator
      const webMercatorPoint = webMercatorUtils.geographicToWebMercator(wgs84Point) as Point;

      return new Graphic({
        geometry: webMercatorPoint,
        attributes: {
          ObjectID: index + 1,
          uuid: symbolData.uuid,
          thumb_url: attrs.thumb_url,
          original_url: attrs.original_url,
          direction: attrs.direction || 0,
          site_uuid: attrs.site_uuid,
          site_name: attrs.site_name,
          report_uuid: attrs.report_uuid,
          report_date: attrs.report_date,
          source_type: attrs.source_type,
          source_uuid: attrs.source_uuid,
          source_name: attrs.source_name,
          created_at: attrs.created_at,
          is_same_location: attrs.is_same_location,
          count: attrs.count,
          picture_uuids: attrs.picture_uuids || attrs.uuid
        }
      });
    });
  }

  /**
   * Create the FeatureLayer with clustering configuration
   */
  private createFeatureLayer(graphics: Graphic[], allSymbols: PictureSymbolData[]): FeatureLayer {
    return new FeatureLayer({
      source: graphics,
      objectIdField: 'ObjectID',
      fields: [
        { name: 'ObjectID', type: 'oid' },
        { name: 'uuid', type: 'string' },
        { name: 'longitude', type: 'double' },
        { name: 'latitude', type: 'double' },
        { name: 'count', type: 'integer' },
        { name: 'is_same_location', type: 'integer' },
        { name: 'picture_uuids', type: 'string' }
      ],
      renderer: {
        type: 'unique-value',
        field: 'uuid',
        uniqueValueInfos: allSymbols.map(s => ({
          value: s.uuid,
          symbol: s.symbol
        })),
        defaultSymbol: this.mapSymbolFactory.createPlaceholderMarkerSymbol()
      } as any,
      visible: true,
      opacity: 1,
      featureReduction: this.getClusteringConfig()
    });
  }

  /**
   * Get clustering configuration for picture layer
   */
  getClusteringConfig(): any {
    return {
      type: 'cluster',
      clusterRadius: '200px',
      clusterMinSize: '24px',
      clusterMaxSize: '60px',
      symbol: {
        type: 'CIMSymbol',
        data: {
          type: 'CIMPointSymbol',
          symbolLayers: [
            {
              type: 'CIMVectorMarker',
              enable: true,
              anchorPoint: { x: 0, y: 0 },
              frame: { xmin: -16, ymin: -16, xmax: 16, ymax: 16 },
              markerGraphics: [
                {
                  type: 'CIMMarkerGraphic',
                  geometry: this.mapSymbolFactory.generateCircleGeometry(0, 0, 16, 64),
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      {
                        type: 'CIMSolidFill',
                        enable: true,
                        color: [16, 54, 214, 255] // #1036D6
                      }
                    ]
                  }
                }
              ]
            },
            {
              type: 'CIMTextMarker',
              enable: true,
              font: {
                family: 'Montserrat',
                style: 'Normal',
                weight: 'Bold',
                size: 14
              },
              horizontalAlignment: 'Center',
              verticalAlignment: 'Middle',
              symbol: {
                type: 'CIMTextSymbol',
                symbolLayers: [
                  {
                    type: 'CIMSolidFill',
                    enable: true,
                    color: [255, 255, 255, 255]
                  }
                ]
              }
            }
          ]
        }
      } as any,
      clusterProperties: {
        total_picture_count: {
          onStatisticField: 'count',
          statisticType: 'sum'
        },
        is_same_location: {
          onStatisticField: 'is_same_location',
          statisticType: 'predominant'
        },
        picture_uuids: {
          onStatisticField: 'picture_uuids',
          statisticType: 'predominant'
        }
      } as any,
      labelingInfo: [{
        deconflictionStrategy: 'none',
        labelExpressionInfo: {
          expression: 'Text($feature.cluster_count, "#,###")'
        },
        symbol: {
          type: 'text',
          color: [255, 255, 255, 255],
          haloColor: [0, 0, 0, 0],
          haloSize: 0,
          font: {
            size: 14,
            family: 'Montserrat',
            weight: 'bold'
          },
          xoffset: 0,
          yoffset: 0
        },
        labelPlacement: 'center-center',
        minScale: 0,
        maxScale: 0
      }]
    } as any;
  }

  /**
   * Setup click handler for picture layer
   */
  private setupClickHandler(
    mapView: __esri.MapView,
    clickContext: PictureClickContext
  ): void {
    mapView.on('click', (event) => {
      mapView.hitTest(event).then(response => {
        const pictureResult = response.results.find((r: any) =>
          r.graphic && r.graphic.layer === this.pictureFeatureLayer
        );

        if (pictureResult) {
          const graphic = (pictureResult as any).graphic;
          if (graphic.attributes?.is_same_location) {
            clickContext.onSameLocationClick(graphic);
          } else {
            clickContext.onPictureClick(graphic, event);
          }
        }
      });
    });
  }

  /**
   * Setup zoom watcher for dynamic clustering
   */
  private setupZoomWatcher(mapView: __esri.MapView): void {
    // Clean up previous watcher
    if (this.zoomWatcherHandle) {
      this.zoomWatcherHandle.remove();
    }

    this.zoomWatcherHandle = reactiveUtils.when(
      () => mapView.stationary === true,
      () => {
        const newZoom = mapView.zoom;

        if (this.pictureFeatureLayer) {
          if (newZoom >= 21) {
            // Disable clustering at extreme zoom
            this.pictureFeatureLayer.featureReduction = null;
          } else if (!this.pictureFeatureLayer.featureReduction) {
            // Re-enable clustering when zooming back out
            this.pictureFeatureLayer.featureReduction = this.getZoomBasedClusteringConfig(newZoom);
          } else {
            // Update cluster radius for intermediate zoom levels
            const featureReduction = this.pictureFeatureLayer.featureReduction as any;
            if (featureReduction.type === 'cluster') {
              featureReduction.clusterRadius = this.calculateClusterRadius(newZoom);
            }
          }
        }
      }
    );
  }

  /**
   * Get zoom-based clustering configuration
   */
  private getZoomBasedClusteringConfig(zoom: number): any {
    return {
      type: 'cluster',
      clusterRadius: this.calculateClusterRadius(zoom),
      clusterMinSize: '32px',
      clusterMaxSize: '32px',
      clusterMinimumSize: 4,
      symbol: {
        type: 'simple-marker',
        size: '32px',
        color: '#1036D6',
        outline: {
          color: 'transparent',
          width: 0
        }
      } as any,
      clusterProperties: {
        total_picture_count: {
          onStatisticField: 'count',
          statisticType: 'sum'
        }
      } as any,
      labelingInfo: [{
        labelExpressionInfo: {
          expression: 'Text($feature.cluster_count, "#")'
        },
        symbol: {
          type: 'text',
          color: [255, 255, 255, 255],
          haloColor: [0, 0, 0, 0],
          haloSize: 0,
          font: {
            size: 14,
            family: 'Montserrat',
            weight: 'bold'
          },
          xoffset: 0,
          yoffset: 0
        },
        labelPlacement: 'center-center',
        minScale: 0,
        maxScale: 0
      }],
      popupTemplate: {
        title: 'Cluster of {cluster_count} pictures',
        content: 'Zoom in to see individual pictures or click to view all'
      }
    } as any;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.zoomWatcherHandle) {
      this.zoomWatcherHandle.remove();
      this.zoomWatcherHandle = null;
    }
    this.pictureFeatureLayer = null;
    this.previousPictureMarkersHash = '';
    this.markerPicturesMap.clear();
  }
}
